import { useCallback, useState } from 'react'
import { emporionToast as toast } from '@admin/toast'
import type { BuilderPage, Section } from '../../types'
import { useI18n } from '../../i18n'
import {
  requestBuilderPageDetail,
  requestBuilderPageSave,
  resolveBuilderPathKey,
} from './builderApi'

type Args = {
  token: string | null
  authHeaders: (withJson?: boolean) => Record<string, string>
  setSection: (section: Section) => void
  loadPages: () => Promise<void>
}

const useBuilderManager = ({ token, authHeaders, setSection, loadPages }: Args) => {
  const { t } = useI18n()
  const [builderPage, setBuilderPage] = useState<BuilderPage | null>(null)
  const [builderLoading, setBuilderLoading] = useState(false)

  const fetchPageDetail = useCallback(
    async (pageId: string): Promise<BuilderPage | null> => {
      if (!token) {
        toast.error(t('builder.toast.loginRequired'))
        return null
      }
      try {
        return await requestBuilderPageDetail({ authHeaders, pageId, t })
      } catch (error) {
        toast.error(error instanceof Error ? error.message : t('builder.toast.loadError'))
        return null
      }
    },
    [authHeaders, t, token],
  )

  const openPageInBuilder = useCallback(
    async (pageId: string) => {
      setBuilderLoading(true)
      const detail = await fetchPageDetail(pageId)
      if (detail) {
        setBuilderPage(detail)
        setSection('builder')
        toast.success(t('builder.toast.loaded'))
      }
      setBuilderLoading(false)
    },
    [fetchPageDetail, setSection, t],
  )

  const saveBuilderPage = useCallback(async () => {
    if (!token) {
      toast.error(t('builder.toast.loginToSave'))
      return
    }
    if (!builderPage) {
      toast.error(t('builder.toast.pickPage'))
      return
    }
    const { pathKey, safeSlug } = resolveBuilderPathKey(builderPage)
    if (!pathKey) {
      toast.error(t('builder.toast.invalidSlug'))
      return
    }
    try {
      const { content: nextContent, slug: nextSlug } = await requestBuilderPageSave({
        authHeaders,
        page: builderPage,
        pathKey,
        safeSlug,
        t,
      })
      setBuilderPage((prev) =>
        prev ? { ...prev, content: nextContent, slug: nextSlug } : prev,
      )
      toast.success(t('builder.toast.saved'))
      await loadPages()
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('builder.toast.saveError'))
    }
  }, [authHeaders, builderPage, loadPages, t, token])

  return {
    builderPage,
    builderLoading,
    setBuilderPage,
    openPageInBuilder,
    saveBuilderPage,
    fetchPageDetail,
  }
}

export default useBuilderManager
